// Context for the runtime job-status catalogue. The Settings modal
// mutates the catalogue via /api/.../statuses; every renderer that
// needs the current label / colour / order / archived state reads
// from this provider so a single fetch fans out across BoardView,
// FilterBar, JobFormFields, StatusBadge, and GanttRow.
//
// Falls back to JOB_STATUSES + STATUS_COLORS before the first fetch
// resolves so the modal/UI render isn't blocked on the API.
import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  JOB_STATUSES,
  type JobStatusDto,
} from "../../../../../../shared/types/production-schedule";
import { useJobStatuses } from "../hooks/useProductionApi";
import { STATUS_COLORS } from "./StatusBadge";

interface Ctx {
  statuses: JobStatusDto[];
  loading: boolean;
  refetch: () => Promise<void>;
}

// Seed the catalogue shape from the canonical JOB_STATUSES so
// consumers can read either source without branching.
function canonicalAsDtos(): JobStatusDto[] {
  return JOB_STATUSES.map((label, i) => ({
    statusKey: label.toLowerCase().replace(/\s+/g, "_"),
    label,
    color: STATUS_COLORS[label] ?? "#707372",
    sortOrder: i,
    archived: false,
    isCustom: false,
    isTerminal: label === "Complete" || label === "Cancelled" || label === "IFA",
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date(0).toISOString(),
  }));
}

const JobStatusesContext = createContext<Ctx>({
  statuses: canonicalAsDtos(),
  loading: false,
  refetch: async () => {},
});

export function JobStatusesProvider({ children }: { children: ReactNode }) {
  const { data, loading, refetch } = useJobStatuses();
  const value: Ctx = useMemo(() => ({
    statuses: data?.statuses ?? canonicalAsDtos(),
    loading,
    refetch,
  }), [data, loading, refetch]);
  return <JobStatusesContext.Provider value={value}>{children}</JobStatusesContext.Provider>;
}

export function useJobStatusesCatalogue(): Ctx {
  return useContext(JobStatusesContext);
}

// Convenience: non-archived statuses in sort order. Used by every
// picker / column generator; the full list is only needed by the
// Settings modal so it can render archived rows too.
export function useActiveJobStatuses(): JobStatusDto[] {
  const { statuses } = useJobStatusesCatalogue();
  return useMemo(
    () => statuses.filter((s) => !s.archived).sort((a, b) => a.sortOrder - b.sortOrder),
    [statuses],
  );
}

// Resolve a status label → DTO row. Used by StatusBadge / GanttRow
// to look up the live colour for a job's `status` string. Returns
// null when the label isn't in the catalogue (custom string typed
// via API but not registered) so callers can fall through to a
// neutral default.
export function useStatusByLabel(): (label: string) => JobStatusDto | null {
  const { statuses } = useJobStatusesCatalogue();
  return useMemo(() => {
    const byLabel = new Map(statuses.map((s) => [s.label, s]));
    return (label: string) => byLabel.get(label) ?? null;
  }, [statuses]);
}
